//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Construction data tab
//
//  Copyright:    © 2021 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                 Initial Revision.
//    002   06.10.23 Sean Flook                 Ensure the OK button is enabled when creating a new record and use colour variables.
//    003   16.10.23 Sean Flook                 Hide the button for the coordinates.
//    004   27.10.23 Sean Flook                 Use new dataFormStyle and removed start and end coordinates as no longer required.
//    005   24.11.23 Sean Flook                 Moved Box and Stack to @mui/system.
//    006   02.01.24 Sean Flook       IMANN-205 Added end date.
//    007   05.01.24 Sean Flook                 Changes to sort out warnings and use CSS shortcuts.
//    008   11.01.24 Sean Flook                 Fix warnings.
//    009   16.01.24 Sean Flook                 Changes required to fix warnings.
//    010   23.01.24 Sean Flook       IMANN-246 Display information when selecting Part Road.
//    011   25.01.24 Sean Flook       IMANN-250 No need to default wholeRoad.
//    012   29.01.24 Sean Flook       IMANN-252 Restrict the characters that can be used in text fields.
//    013   05.02.24 Sean Flook                 Filter available districts by the organisation.
//    014   07.02.24 Sean Flook                 Display a warning dialog when changing from Part Road to Whole Road.
//    015   13.02.24 Sean Flook                 Set the ADSWholeRoadControl variant.
//    016   13.02.24 Sean Flook                 Updated to new colour.
//    017   15.02.24 Joel Benford     IMANN-299 Toolbar changes
//    018   07.03.24 Sean Flook       IMANN-348 Changes required to ensure the OK button is correctly enabled and removed redundant code.
//    019   11.03.24 Sean Flook           GLB12 Adjusted height to remove gap.
//    020   13.03.24 Joshua McCormick IMANN-280 Added dataTabToolBar for inner toolbar styling
//    021   22.03.24 Sean Flook           GLB12 Changed to use dataFormStyle so height can be correctly set.
//    022   27.03.24 Sean Flook                 Clear specific location if going back to whole road.
//    023   20.06.24 Sean Flook       IMANN-636 Use the new user rights.
//    024   26.07.24 Sean Flook       IMANN-856 Correctly handle deleting newly added record.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    025   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    026   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.6.4
//    027   30.09.25 Sean Flook       IMANN-2066 When changing from Part Road to Whole Road, reset the coordinates.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    028   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    029   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    030   28.10.25 Sean Flook       IMANN-2112 Always keep the OK and Cancel buttons at the bottom of the tab.
//    031   28.10.25 Sean Flook       IMANN-2105 When the save is disabled disable the Ok button.
//    032   17.12.25 Sean Flook       IMANN-2196 Removed the information component as no longer required in this control.
//    033   17.12.25 Sean Flook       IMANN-1705 Removed old debug message.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import useSandboxStore from "../context/SandboxStore";

import LookupContext from "../context/lookupContext";
import UserContext from "./../context/userContext";
import MapContext from "./../context/mapContext";
import InformationContext from "../context/informationContext";
import StreetContext from "../context/streetContext";

import { GetLookupLabel, filteredLookup } from "../utils/HelperUtils";
import { filteredOperationalDistricts } from "../utils/StreetUtils";
import ObjectComparison, { constructionKeysToIgnore } from "../utils/ObjectComparison";

import { ConvertDate, UpdateSandbox } from "../utils/HelperUtils";

import { Avatar, Typography, Box, Stack } from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";
import ADSSelectControl from "../components/ADSSelectControl";
import ADSDateControl from "../components/ADSDateControl";
import ADSWholeRoadControl from "../components/ADSWholeRoadControl";
import ADSTextControl from "../components/ADSTextControl";
import ADSOkCancelControl from "../components/ADSOkCancelControl";
import ADSSwitchControl from "../components/ADSSwitchControl";
import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";
import MessageDialog from "../dialogs/MessageDialog";

import ConstructionType from "../data/ConstructionType";
import AggregateAbrasionValue from "../data/AggregateAbrasionValue";
import PolishedStoneValue from "../data/PolishedStoneValue";
import SwaOrgRef from "../data/SwaOrgRef";
import ReinstatementType from "../data/ReinstatementType";

import { Texture } from "@mui/icons-material";
import { adsWhite, adsDarkPurple } from "../utils/ADSColours";
import { toolbarStyle, dataTabToolBar, dataFormStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

ConstructionDataTab.propTypes = {
  data: PropTypes.object,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  focusedField: PropTypes.string,
  saveDisabled: PropTypes.bool,
  onHomeClick: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function ConstructionDataTab({ data, errors, loading, focusedField, saveDisabled, onHomeClick, onAdd, onDelete }) {
  const theme = useTheme();

  const sandboxStore = useSandboxStore((state) => state);
  const sourceStreet = useSandboxStore((state) => state.sourceStreet);
  const currentConstructionRecord = useSandboxStore((state) => state.currentConstructionRecord);
  const setStore = useSandboxStore((state) => state.setStore);

  const lookupContext = useContext(LookupContext);
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const informationContext = useContext(InformationContext);
  const streetContext = useContext(StreetContext);

  const [dataChanged, setDataChanged] = useState(false);

  const [swaOrgRefLookup, setSwaOrgRefLookup] = useState(filteredLookup(SwaOrgRef, false));
  const [reinstatementTypeLookup, setReinstatementTypeLookup] = useState(filteredLookup(ReinstatementType, false));

  const [constructionType, setConstructionType] = useState(null);
  const [reinstatementType, setReinstatementType] = useState(null);
  const [aggregateAbrasionValue, setAggregateAbrasionValue] = useState(null);
  const [polishedStoneValue, setPolishedStoneValue] = useState(null);
  const [frostHeaveSusceptibility, setFrostHeaveSusceptibility] = useState(false);
  const [steppedJoint, setSteppedJoint] = useState(false);
  const [constructionDescription, setConstructionDescription] = useState("");
  const [organisation, setOrganisation] = useState(null);
  const [district, setDistrict] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [wholeRoad, setWholeRoad] = useState(true);
  const [specifyLocation, setSpecifyLocation] = useState("");
  const [constructionStartX, setConstructionStartX] = useState(0);
  const [constructionStartY, setConstructionStartY] = useState(0);
  const [constructionEndX, setConstructionEndX] = useState(0);
  const [constructionEndY, setConstructionEndY] = useState(0);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [showWholeRoadWarning, setShowWholeRoadWarning] = useState(false);

  const [constructionTypeError, setConstructionTypeError] = useState(null);
  const [reinstatementTypeError, setReinstatementTypeError] = useState(null);
  const [aggregateAbrasionValueError, setAggregateAbrasionValueError] = useState(null);
  const [polishedStoneValueError, setPolishedStoneValueError] = useState(null);
  const [frostHeaveSusceptibilityError, setFrostHeaveSusceptibilityError] = useState(null);
  const [steppedJointError, setSteppedJointError] = useState(null);
  const [constructionDescriptionError, setConstructionDescriptionError] = useState(null);
  const [organisationError, setOrganisationError] = useState(null);
  const [districtError, setDistrictError] = useState(null);
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);
  const [wholeRoadError, setWholeRoadError] = useState(null);
  const [specifyLocationError, setSpecifyLocationError] = useState(null);

  /**
   * Method used to update the current record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|boolean|Date|number|null} newValue The value used to update the given field.
   */
  const RefreshSandboxData = (field, newValue) => {
    const newConstructionData = GetCurrentData(field, newValue);
    setStore(UpdateSandbox(sandboxStore, "construction", newConstructionData));
  };

  /**
   * Event to handle when the construction type is changed.
   *
   * @param {number|null} newValue The new construction type.
   */
  const handleConstructionTypeChangeEvent = (newValue) => {
    setConstructionType(newValue);
    RefreshSandboxData("constructionType", newValue);
  };

  /**
   * Event to handle when the reinstatement type is changed.
   *
   * @param {number|null} newValue The new reinstatement type.
   */
  const handleReinstatementTypeChangeEvent = (newValue) => {
    setReinstatementType(newValue);
    RefreshSandboxData("reinstatementType", newValue);
  };

  /**
   * Event to handle when the aggregate abrasion value is changed.
   *
   * @param {number|null} newValue The new aggregate abrasion value.
   */
  const handleAggregateAbrasionValueChangeEvent = (newValue) => {
    setAggregateAbrasionValue(newValue);
    RefreshSandboxData("aggregateAbrasionValue", newValue);
  };

  /**
   * Event to handle when the polished stone value is changed.
   *
   * @param {number|null} newValue The new polished stone value.
   */
  const handlePolishedStoneValueChangeEvent = (newValue) => {
    setPolishedStoneValue(newValue);
    RefreshSandboxData("polishedStoneValue", newValue);
  };

  /**
   * Event to handle when the frost heave susceptibility is changed.
   *
   * @param {number|null} newValue The new frost heave susceptibility.
   */
  const handleFrostHeaveSusceptibilityChangeEvent = (newValue) => {
    setFrostHeaveSusceptibility(newValue);
    RefreshSandboxData("frostHeaveSusceptibility", newValue);
  };

  /**
   * Event to handle when the stepped joint is changed.
   *
   * @param {number|null} newValue The new stepped joint.
   */
  const handleSteppedJointChangeEvent = (newValue) => {
    setSteppedJoint(newValue);
    RefreshSandboxData("steppedJoint", newValue);
  };

  /**
   * Event to handle when the description is changed.
   *
   * @param {string|null} newValue The new description.
   */
  const handleConstructionDescriptionChangeEvent = (newValue) => {
    setConstructionDescription(newValue);
    RefreshSandboxData("constructionDescription", newValue);
  };

  /**
   * Event to handle when the organisation is changed.
   *
   * @param {number|null} newValue The new organisation.
   */
  const handleOrganisationChangeEvent = (newValue) => {
    setOrganisation(newValue);
    RefreshSandboxData("organisation", newValue);
  };

  /**
   * Event to handle when the district is changed.
   *
   * @param {number|null} newValue The new district.
   */
  const handleDistrictChangeEvent = (newValue) => {
    setDistrict(newValue);
    RefreshSandboxData("district", newValue);
  };

  /**
   * Event to handle when the start date is changed.
   *
   * @param {Date|null} newValue The new start date.
   */
  const handleStartDateChangeEvent = (newValue) => {
    setStartDate(newValue);
    RefreshSandboxData("startDate", newValue);
  };

  /**
   * Event to handle when the end date is changed.
   *
   * @param {Date|null} newValue The new end date.
   */
  const handleEndDateChangeEvent = (newValue) => {
    setEndDate(newValue);
    RefreshSandboxData("endDate", newValue);
  };

  /**
   * Event to handle when the whole road flag is changed.
   *
   * @param {boolean} newValue The new whole road flag.
   */
  const handleWholeRoadChangeEvent = (newValue) => {
    if (newValue && !wholeRoad) {
      setShowWholeRoadWarning(true);
    } else {
      setWholeRoad(newValue);
      RefreshSandboxData("wholeRoad", newValue);
      if (newValue) {
        mapContext.onEditMapObject(null, null);
        informationContext.onClearInformation();
      } else {
        mapContext.onEditMapObject(62, data && data.constructionData && data.constructionData.pkId);
        informationContext.onDisplayInformation("partRoadASD", "ConstructionDataTab");
      }
    }
  };

  /**
   * Event to handle when the specific location is changed.
   *
   * @param {string|null} newValue The new specific location.
   */
  const handleSpecifyLocationChangeEvent = (newValue) => {
    setSpecifyLocation(newValue);
    RefreshSandboxData("specifyLocation", newValue);
  };

  /**
   * Event to handle when the home button is clicked.
   */
  const handleHomeClick = () => {
    const sourceConstruction =
      data.pkId > 0 && sourceStreet ? sourceStreet.constructions.find((x) => x.pkId === data.pkId) : null;

    if (onHomeClick)
      onHomeClick(
        dataChanged ? (currentConstructionRecord ? "check" : "discard") : "discard",
        sourceConstruction,
        currentConstructionRecord
      );
  };

  /**
   * Event to handle when the OK button is clicked.
   */
  const handleOkClicked = () => {
    if (onHomeClick) onHomeClick("save", null, currentConstructionRecord);
  };

  /**
   * Event to handle when the Cancel button is clicked.
   */
  const handleCancelClicked = () => {
    if (dataChanged) {
      if (data && data.constructionData) {
        setConstructionType(data.constructionData.constructionType);
        setReinstatementType(data.constructionData.reinstatementTypeCode);
        setAggregateAbrasionValue(data.constructionData.aggregateAbrasionVal);
        setPolishedStoneValue(data.constructionData.polishedStoneVal);
        setFrostHeaveSusceptibility(data.constructionData.frostHeaveSusceptibility);
        setSteppedJoint(data.constructionData.steppedJoint);
        setConstructionDescription(data.constructionData.constructionDescription);
        setOrganisation(data.constructionData.swaOrgRefConsultant);
        setDistrict(data.constructionData.districtRefConsultant);
        setStartDate(data.constructionData.recordStartDate);
        setEndDate(data.constructionData.recordEndDate);
        setWholeRoad(data.constructionData.wholeRoad);
        setSpecifyLocation(data.constructionData.specificLocation ? data.constructionData.specificLocation : "");
        setConstructionStartX(data.constructionData.constructionStartX ? data.constructionData.constructionStartX : 0);
        setConstructionStartY(data.constructionData.constructionStartY ? data.constructionData.constructionStartY : 0);
        setConstructionEndX(data.constructionData.constructionEndX ? data.constructionData.constructionEndX : 0);
        setConstructionEndY(data.constructionData.constructionEndY ? data.constructionData.constructionEndY : 0);
      }
    }
    if (onHomeClick) onHomeClick("discard", data.constructionData, null);
  };

  /**
   * Method to return the current construction record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|boolean|Date|number|null} newValue The value used to update the given field.
   * @returns {object} The current construction record.
   */
  function GetCurrentData(field, newValue) {
    return {
      changeType:
        field && field === "changeType"
          ? newValue
          : !data.constructionData.pkId || data.constructionData.pkId < 0
          ? "I"
          : "U",
      usrn: data.constructionData.usrn,
      seqNum: data.constructionData.seqNum,
      wholeRoad: field && field === "wholeRoad" ? newValue : wholeRoad,
      specificLocation:
        field && field === "specifyLocation"
          ? newValue
          : field && field === "wholeRoad" && newValue
          ? ""
          : specifyLocation,
      neverExport: data.constructionData.neverExport,
      recordStartDate:
        field && field === "startDate" ? newValue && ConvertDate(newValue) : startDate && ConvertDate(startDate),
      recordEndDate: field && field === "endDate" ? newValue && ConvertDate(newValue) : endDate && ConvertDate(endDate),
      reinstatementTypeCode: field && field === "reinstatementType" ? newValue : reinstatementType,
      constructionType: field && field === "constructionType" ? newValue : constructionType,
      aggregateAbrasionVal: field && field === "aggregateAbrasionValue" ? newValue : aggregateAbrasionValue,
      polishedStoneVal: field && field === "polishedStoneValue" ? newValue : polishedStoneValue,
      frostHeaveSusceptibility: field && field === "frostHeaveSusceptibility" ? newValue : frostHeaveSusceptibility,
      steppedJoint: field && field === "steppedJoint" ? newValue : steppedJoint,
      asdCoordinate: data.constructionData.asdCoordinate,
      asdCoordinateCount: data.constructionData.asdCoordinateCount,
      constructionStartX:
        field && field === "constructionStartX"
          ? newValue
          : field && field === "wholeRoad" && newValue
          ? 0
          : constructionStartX,
      constructionStartY:
        field && field === "constructionStartY"
          ? newValue
          : field && field === "wholeRoad" && newValue
          ? 0
          : constructionStartY,
      constructionEndX:
        field && field === "constructionEndX"
          ? newValue
          : field && field === "wholeRoad" && newValue
          ? 0
          : constructionEndX,
      constructionEndY:
        field && field === "constructionEndY"
          ? newValue
          : field && field === "wholeRoad" && newValue
          ? 0
          : constructionEndY,
      constructionDescription: field && field === "constructionDescription" ? newValue : constructionDescription,
      swaOrgRefConsultant: field && field === "organisation" ? newValue : organisation,
      districtRefConsultant: field && field === "district" ? newValue : district,
      wktGeometry: data.constructionData.wktGeometry,
      pkId: data.constructionData.pkId,
      entryDate: data.constructionData.entryDate,
      lastUpdateDate: data.constructionData.lastUpdateDate,
    };
  }

  /**
   * Event to handle when the add construction button is clicked.
   */
  const handleAddConstruction = () => {
    if (onAdd) onAdd();
  };

  /**
   * Event to handle when the delete construction button is clicked.
   */
  const handleDeleteConstruction = () => {
    setOpenDeleteConfirmation(true);
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the deletion; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    const pkId = data && data.pkId ? data.pkId : -1;

    if (deleteConfirmed && pkId) {
      const currentData = GetCurrentData("changeType", "D");
      if (onHomeClick) onHomeClick("save", null, currentData);
      if (onDelete) onDelete(pkId);
    }
  };

  /**
   * Event to handle when the message dialog is closed.
   *
   * @param {string} action The action taken from the message dialog.
   */
  const handleCloseMessageDialog = (action) => {
    if (action === "continue") {
      setWholeRoad(true);
      setSpecifyLocation("");
      setConstructionStartX(0);
      setConstructionStartY(0);
      setConstructionEndX(0);
      setConstructionEndY(0);
      RefreshSandboxData("wholeRoad", true);

      mapContext.onEditMapObject(null, null);
      informationContext.onClearInformation();
    }

    setShowWholeRoadWarning(false);
  };

  useEffect(() => {
    if (!loading && data && data.constructionData) {
      setConstructionType(data.constructionData.constructionType);
      setReinstatementType(data.constructionData.reinstatementTypeCode);
      setAggregateAbrasionValue(data.constructionData.aggregateAbrasionVal);
      setPolishedStoneValue(data.constructionData.polishedStoneVal);
      setFrostHeaveSusceptibility(data.constructionData.frostHeaveSusceptibility);
      setSteppedJoint(data.constructionData.steppedJoint);
      setConstructionDescription(data.constructionData.constructionDescription);
      setOrganisation(data.constructionData.swaOrgRefConsultant);
      setDistrict(data.constructionData.districtRefConsultant);
      setStartDate(data.constructionData.recordStartDate);
      setEndDate(data.constructionData.recordEndDate);
      setWholeRoad(data.constructionData.wholeRoad);
      setSpecifyLocation(data.constructionData.specificLocation ? data.constructionData.specificLocation : "");
      setConstructionStartX(data.constructionData.constructionStartX ? data.constructionData.constructionStartX : 0);
      setConstructionStartY(data.constructionData.constructionStartY ? data.constructionData.constructionStartY : 0);
      setConstructionEndX(data.constructionData.constructionEndX ? data.constructionData.constructionEndX : 0);
      setConstructionEndY(data.constructionData.constructionEndY ? data.constructionData.constructionEndY : 0);

      setSwaOrgRefLookup(filteredLookup(SwaOrgRef, false));
      setReinstatementTypeLookup(filteredLookup(ReinstatementType, false));
    }
  }, [loading, data]);

  useEffect(() => {
    if (
      !wholeRoad &&
      !informationContext.informationType &&
      informationContext.informationSource !== "partRoadASD" &&
      informationContext.informationSource !== "ADSAppBar"
    ) {
      informationContext.onDisplayInformation("partRoadASD", "ConstructionDataTab");
    }
  }, [wholeRoad, informationContext]);

  useEffect(() => {
    if (sourceStreet && currentConstructionRecord) {
      const sourceConstruction = sourceStreet.constructions.find((x) => x.pkId === currentConstructionRecord.pkId);

      if (sourceConstruction) {
        setDataChanged(!ObjectComparison(sourceConstruction, currentConstructionRecord, constructionKeysToIgnore));
      } else if (currentConstructionRecord.pkId < 0) setDataChanged(true);
    }
  }, [sourceStreet, currentConstructionRecord]);

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editASD))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editASD);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    setConstructionTypeError(null);
    setReinstatementTypeError(null);
    setAggregateAbrasionValueError(null);
    setPolishedStoneValueError(null);
    setFrostHeaveSusceptibilityError(null);
    setSteppedJointError(null);
    setConstructionDescriptionError(null);
    setOrganisationError(null);
    setDistrictError(null);
    setStartDateError(null);
    setEndDateError(null);
    setWholeRoadError(null);
    setSpecifyLocationError(null);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        switch (error.field.toLowerCase()) {
          case "constructiontype":
            setConstructionTypeError(error.errors);
            break;

          case "reinstatementtypecode":
            setReinstatementTypeError(error.errors);
            break;

          case "aggregateabrasionval":
            setAggregateAbrasionValueError(error.errors);
            break;

          case "polishedstoneval":
            setPolishedStoneValueError(error.errors);
            break;

          case "frostheavesusceptibility":
            setFrostHeaveSusceptibilityError(error.errors);
            break;

          case "steppedjoint":
            setSteppedJointError(error.errors);
            break;

          case "constructiondescription":
            setConstructionDescriptionError(error.errors);
            break;

          case "swaorgrefconsultant":
            setOrganisationError(error.errors);
            break;

          case "districtrefconsultant":
            setDistrictError(error.errors);
            break;

          case "recordstartdate":
            setStartDateError(error.errors);
            break;

          case "recordenddate":
            setEndDateError(error.errors);
            break;

          case "wholeroad":
            setWholeRoadError(error.errors);
            break;

          case "specificlocation":
            setSpecifyLocationError(error.errors);
            break;

          default:
            break;
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    if (saveDisabled && dataChanged) setDataChanged(false);
  }, [saveDisabled, dataChanged]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-street-construction-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={dataTabToolBar}>
          <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
            {streetContext.currentRecord.type === 62 && streetContext.currentRecord.newRecord ? (
              <ADSActionButton
                variant="close"
                tooltipTitle="Close"
                tooltipPlacement="bottom"
                onClick={handleCancelClicked}
              />
            ) : (
              <ADSActionButton variant="home" tooltipTitle="Home" tooltipPlacement="bottom" onClick={handleHomeClick} />
            )}
            <Typography
              sx={{
                flexGrow: 1,
                display: "none",
                [theme.breakpoints.up("sm")]: {
                  display: "block",
                },
              }}
              variant="subtitle1"
              noWrap
              align="left"
            >
              |
            </Typography>
            <Avatar
              variant="rounded"
              sx={{
                height: theme.spacing(2),
                width: theme.spacing(2),
                color: adsWhite,
                clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
                backgroundColor: adsDarkPurple,
              }}
            >
              <Texture
                sx={{
                  height: theme.spacing(2),
                  width: theme.spacing(2),
                }}
              />
            </Avatar>
            <Typography
              sx={{
                flexGrow: 1,
                display: "none",
                [theme.breakpoints.up("sm")]: {
                  display: "block",
                },
              }}
              variant="subtitle1"
              noWrap
              align="left"
            >
              {streetContext.currentRecord.type === 62 && streetContext.currentRecord.newRecord
                ? "Add new construction"
                : `Construction (${data.index + 1} of ${data.totalRecords})`}
            </Typography>
          </Stack>
          {!(streetContext.currentRecord.type === 62 && streetContext.currentRecord.newRecord) && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
              <ADSActionButton
                variant="delete"
                disabled={!userCanEdit}
                tooltipTitle="Delete construction record"
                tooltipPlacement="right"
                onClick={handleDeleteConstruction}
              />
              <ADSActionButton
                variant="add"
                disabled={!userCanEdit}
                tooltipTitle="Add new construction record"
                tooltipPlacement="right"
                onClick={handleAddConstruction}
              />
            </Stack>
          )}
        </Stack>
      </Box>
      <Box sx={dataFormStyle("ConstructionDataTab")}>
        <ADSSelectControl
          label="Construction type"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "ConstructionType" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={ConstructionType}
          lookupId="id"
          lookupLabel={GetLookupLabel(false)}
          lookupColour="colour"
          value={constructionType}
          errorText={constructionTypeError}
          onChange={handleConstructionTypeChangeEvent}
          helperText="The type of Construction that the Record applies to."
        />
        <ADSSelectControl
          label="Reinstatement type"
          isEditable={userCanEdit}
          isRequired={constructionType === 1}
          isFocused={focusedField ? focusedField === "ReinstatementTypeCode" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={reinstatementTypeLookup}
          lookupId="id"
          lookupLabel={GetLookupLabel(false)}
          lookupColour="colour"
          value={reinstatementType}
          errorText={reinstatementTypeError}
          onChange={handleReinstatementTypeChangeEvent}
          helperText="Reinstatement as defined in the SROH codes of practice."
        />
        <ADSSelectControl
          label="Aggregate abrasion value"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "AggregateAbrasionVal" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={
            reinstatementType && AggregateAbrasionValue.filter((x) => x.reinstatementCode === reinstatementType)
          }
          lookupId="id"
          lookupLabel={GetLookupLabel(false)}
          value={aggregateAbrasionValue}
          errorText={aggregateAbrasionValueError}
          onChange={handleAggregateAbrasionValueChangeEvent}
          helperText="Value as defined in the SROH codes of practice."
        />
        <ADSSelectControl
          label="Polished stone value"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "PolishedStoneVal" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={reinstatementType && PolishedStoneValue.filter((x) => x.reinstatementCode === reinstatementType)}
          lookupId="id"
          lookupLabel={GetLookupLabel(false)}
          value={polishedStoneValue}
          errorText={polishedStoneValueError}
          onChange={handlePolishedStoneValueChangeEvent}
          helperText="Value as defined in the SROH codes of practice."
        />
        <ADSSwitchControl
          label="Frost heave susceptibility"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "FrostHeaveSusceptibility" : false}
          loading={loading}
          checked={frostHeaveSusceptibility}
          trueLabel="Yes"
          falseLabel="No"
          errorText={frostHeaveSusceptibilityError}
          // helperText="Set this if you do not want this street to be included in any exports."
          onChange={handleFrostHeaveSusceptibilityChangeEvent}
        />
        <ADSSwitchControl
          label="Stepped joint"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "SteppedJoint" : false}
          loading={loading}
          checked={steppedJoint}
          trueLabel="Yes"
          falseLabel="No"
          errorText={steppedJointError}
          // helperText="Set this if you do not want this street to be included in any exports."
          onChange={handleSteppedJointChangeEvent}
        />
        <ADSTextControl
          label="Description"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "ConstructionDescription" : false}
          loading={loading}
          value={constructionDescription}
          maxLength={250}
          minLines={3}
          maxLines={5}
          id="construction-description"
          characterSet="GeoPlaceStreet1"
          errorText={constructionDescriptionError}
          helperText="Description providing additional Construction information for certain definitions."
          onChange={handleConstructionDescriptionChangeEvent}
        />
        <ADSSelectControl
          label="Organisation"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "SwaOrgRefConsultant" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={swaOrgRefLookup}
          lookupId="id"
          lookupLabel={GetLookupLabel(false)}
          lookupColour="colour"
          value={organisation}
          errorText={organisationError}
          onChange={handleOrganisationChangeEvent}
          helperText="Code to identify the Highway Authority which must be consulted about the Construction."
        />
        <ADSSelectControl
          label="District"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "DistrictRefConsultant" : false}
          loading={loading}
          useRounded
          includeHistoric
          lookupData={filteredOperationalDistricts(lookupContext.currentLookups.operationalDistricts, organisation)}
          lookupId="districtId"
          lookupLabel="districtName"
          value={district}
          errorText={districtError}
          onChange={handleDistrictChangeEvent}
          helperText="Code to identify the Operational District of the Highway Authority which must be consulted about the Construction."
        />
        <ADSDateControl
          label="Start date"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "RecordStartDate" : false}
          loading={loading}
          value={startDate}
          helperText="Date when the Record started."
          errorText={startDateError}
          onChange={handleStartDateChangeEvent}
        />
        <ADSDateControl
          label="End date"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "RecordEndDate" : false}
          loading={loading}
          value={endDate}
          helperText="Date when the Record ends."
          errorText={endDateError}
          onChange={handleEndDateChangeEvent}
        />
        <ADSWholeRoadControl
          variant="WholeRoad"
          label="Applied to"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "WholeRoad" : false}
          loading={loading}
          value={wholeRoad}
          helperText="Indicator as to whether the Construction Record applies to the Whole Road."
          errorText={wholeRoadError}
          onChange={handleWholeRoadChangeEvent}
        />
        {!wholeRoad && (
          <ADSTextControl
            label="Specify location"
            isEditable={userCanEdit}
            isRequired
            isFocused={focusedField ? focusedField === "SpecificLocation" : false}
            loading={loading}
            value={specifyLocation}
            maxLength={250}
            minLines={3}
            maxLines={5}
            id="construction-specify-location"
            characterSet="GeoPlaceStreet1"
            errorText={specifyLocationError}
            helperText="Description of location of the part or parts of the Street for which this Construction type is applicable."
            onChange={handleSpecifyLocationChangeEvent}
          />
        )}
      </Box>
      <ADSOkCancelControl
        okDisabled={!dataChanged}
        onOkClicked={handleOkClicked}
        onCancelClicked={handleCancelClicked}
      />
      <div>
        <ConfirmDeleteDialog
          variant="construction"
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
        <MessageDialog isOpen={showWholeRoadWarning} variant="cancelASDPartRoad" onClose={handleCloseMessageDialog} />
      </div>
    </Fragment>
  );
}

export default ConstructionDataTab;
